import {
	AsyncPipe,
	DatePipe,
	NgClass,
	NgFor,
	NgIf,
	NgSwitch,
	NgSwitchCase,
	NgSwitchDefault
} from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
	ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	UserNotificationModel,
	UserNotificationTypeModel,
	UserTaggedInCommentPayload
} from '@twrx-api-models';
import { RouteParams, SubmissionOverviewRoutes } from 'app/common/routes';
import { Observable } from 'rxjs';
import { BaseApiComponent } from 'app/common/base/base-api-component';
import { listItemTooltipShowDelay } from 'app/common/app-constants';
import { NotificationGroups } from 'app/common/notification.constants';
import { NotificationSubscriptionModel } from 'app/models/dto/notification-subscription';
import { buildGroups } from 'app/common/utils/utils.notification.groups';
import { DialogMenuComponent } from 'app/shared/components/dialog-menu/dialog-menu.component';
import {
	subscribeToGroups,
	unSubscribeFromGroups
} from 'app/store/account/account.actions';
import {
	deleteAllReadUserNotifications,
	deleteUserNotifications,
	loadUnreadCount,
	loadUserNotifications,
	markAllUserNotificationsRead,
	markUserNotificationsRead,
	UserNotificationsActions
} from 'app/store/user-notifications/user-notifications.actions';
import {
	selectUserNotificationItems,
	selectUserNotificationsHasRead,
	selectUserNotificationsUnreadCount
} from 'app/store/user-notifications/user-notifications.selectors';
import { keysToCamelCase } from 'app/common/utils/utils.object';

@UntilDestroy()
@Component({
	selector:        'app-user-notifications',
	templateUrl:     './user-notifications.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation:   ViewEncapsulation.None,
	imports:         [
		AsyncPipe,
		DatePipe,
		DialogMenuComponent,
		MatBadge,
		MatButton,
		MatDivider,
		MatIcon,
		MatIconButton,
		MatMenu,
		MatMenuTrigger,
		MatTooltip,
		NgClass,
		NgFor,
		NgIf,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		TranslocoPipe
	]
})
export class UserNotificationsComponent
	extends BaseApiComponent
	implements OnInit, OnDestroy
{
	public readonly listItemTooltipShowDelay: number = listItemTooltipShowDelay;
	public readonly NotificationType = UserNotificationTypeModel;

	public items$:       Observable<UserNotificationModel[]>;
	public unreadCount$: Observable<number>;
	public hasRead$:     Observable<boolean>;

	private groups: NotificationSubscriptionModel[] = [];

	constructor(
		private readonly store: Store,
		private readonly router: Router,
		private readonly actions: Actions
	) {
		super();
	}

	ngOnInit(): void {
		this.items$ = this.store.select(selectUserNotificationItems);
		this.unreadCount$ = this.store.select(
			selectUserNotificationsUnreadCount
		);
		this.hasRead$ = this.store.select(selectUserNotificationsHasRead);

		this.groups = buildGroups([NotificationGroups.UserTaggedInComment]);

		this.store.dispatch(
			subscribeToGroups(this.createRequestPayload(this.groups))
		);

		this.store.dispatch(loadUnreadCount(this.createRequestPayload()));
		this.refreshList();

		this.actions
			.pipe(
				ofType(UserNotificationsActions.UserNotificationReceived),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.refreshList();
			});
	}

	ngOnDestroy(): void {
		this.store.dispatch(
			unSubscribeFromGroups(this.createRequestPayload(this.groups))
		);

		super.ngOnDestroy();
	}

	public onClick(item: UserNotificationModel): void {
		if (!item.isRead) {
			this.store.dispatch(
				markUserNotificationsRead(this.createRequestPayload([item.id]))
			);
		}

		if (item.type === UserNotificationTypeModel.UserTaggedInComment) {
			const payload = this.getTaggedPayload(item);

			if (payload?.submissionPeriodId && payload?.customerId) {
				this.router.navigate([SubmissionOverviewRoutes.Root], {
					queryParams: {
						[RouteParams.SubmissionPeriodIdParam]:
							payload.submissionPeriodId,
						[RouteParams.CustomerIdParam]: payload.customerId
					}
				});
			}
		}
	}

	public getTaggedPayload(
		item: UserNotificationModel
	): UserTaggedInCommentPayload | null {
		if (
			item.type !== UserNotificationTypeModel.UserTaggedInComment
			|| !item.payload
		) {
			return null;
		}

		try {
			return keysToCamelCase(
				JSON.parse(item.payload)
			) as UserTaggedInCommentPayload;
		} catch {
			return null;
		}
	}

	public markAllRead(): void {
		this.store.dispatch(
			markAllUserNotificationsRead(this.createRequestPayload())
		);
	}

	public deleteItem(item: UserNotificationModel, event: MouseEvent): void {
		event.stopPropagation();

		this.store.dispatch(
			deleteUserNotifications(this.createRequestPayload(item.id))
		);
	}

	public deleteAllRead(): void {
		this.store.dispatch(
			deleteAllReadUserNotifications(this.createRequestPayload())
		);
	}

	public refreshList(): void {
		this.store.dispatch(
			loadUserNotifications(
				this.createRequestPayload({
					pageIndex:  0,
					pageSize:   20,
					unreadOnly: false
				})
			)
		);
		this.store.dispatch(loadUnreadCount(this.createRequestPayload()));
	}
}
